import type { PurchaseService } from "@highmarch/app";
import type { LevelCatalog } from "@highmarch/app";
import { fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { resourceIconUrl } from "../portraits";
import type { HubApi, ShopCurrencyPage } from "../../features/registry";
import { fmtSpeedUp } from "./ordersSheet";
import { amountTier } from "../deals/dealsModel";
import { priceButtonHtml, secHtml, slotTileHtml } from "../components";
import { GemPrice, PackCard } from "./shopModel";
import { openShops } from "../shops/shopsScreen";
import { shopsLink } from "../shops/shopsLink";
import type { ShopTabId } from "../shops/shopsModel";
import "../styles/offerPolish.css";
import "../styles/gemSinks.css";

/**
 * The Shop tab's entry and the gem goods' markup. Shop redesign (owner 2026-10-19, Kingshot-style): the four currency
 * pages (Gold · Gems · Marks · Essence — mostly links to where things are bought) are gone; the Shop tab opens the one
 * Shop sheet (ui/shops/shopsScreen.ts: Merchant · Supplies · Event · VIP · Trophies · Skins). Builders became
 * real-money packs (Store → Builders); gold packs, bought with gems, are Shop → Supplies (owner 2026-10-21: the Shop
 * spends currency, the Store takes real money). What stays here is shared: the deps the gem goods' buttons need
 * (ShopActions), and the speed-up / gold pack tiles.
 */

export interface ShopDeps {
  readonly hub: HubApi;
  readonly catalog: LevelCatalog;
  now(): number;
  readonly purchases: PurchaseService | null;
  /** Which store sells gem packs: the web's test store gets its own confirm and label. */
  readonly storeKind: "sandbox" | "storekit2" | null;
}

/** Which Shop tab a currency's old page lands on (gold packs, a pet's Treats and the battle supplies are Supplies). */
const PAGE_TAB: Readonly<Record<ShopCurrencyPage, ShopTabId | null>> = { gold: "supplies", gems: "supplies", marks: "merchant", essence: null };

/** Opens the Shop sheet (the Shop tab: the first open tab; a currency's old page: the tab that sells for it). */
export function openShopHub(d: ShopDeps, page: ShopCurrencyPage = "gold"): HTMLElement | null {
  const l = shopsLink();
  if (l) l.hub = d.hub;
  return openShops(PAGE_TAB[page]);
}

/** A gem price button: always the gold primary (STYLE rule 3); short of gems, the amount turns red and the tap says why. */
export function gemBtnHtml(p: GemPrice, cls: string, data: string, label = ""): string {
  return priceButtonHtml({ currency: "gems", amount: fmtCompact(p.gems), label, short: !p.affordable, className: `ebuy ${cls}`, data });
}

/** Speed-ups (the hourglass, its length on the cap) and gold packs (what they hold, "Left: n today"). */
export function packsHtml(title: string, ic: string, sub: string, packs: readonly PackCard[]): string {
  if (packs.length === 0) return "";
  const card = (p: PackCard) => {
    if (p.free) {
      // the day's free gold pack (owner 2026-10-21; it replaced the free Supply Crate): a FREE star and a green strip
      const gold = p.contents ?? 0;
      return slotTileHtml({
        well: { art: resourceIconUrl("gold"), count: fmtCompact(gold), rarity: gold < 1_000 ? 2 : gold < 3_000 ? 3 : 4 }, name: "Gold",
        state: p.free.open ? "ok" : "sold", left: p.free.open ? "Free today" : "Back tomorrow", stamp: "Claimed",
        price: p.free.open ? priceButtonHtml({ currency: "real", amount: "FREE", className: "ebuy b-freepack b-free" }) : "",
        className: `pack k-resources free${p.free.open ? "" : " sold"}`, data: `data-id="${p.id}"`,
      }).replace(`<div class="k-well`, p.free.open ? `<span class="k-free">FREE</span><div class="k-well` : `<div class="k-well`);
    }
    const sold = p.left === 0, locked = p.opensAt !== null;
    // a gold pack names itself and shows its gold as the well's count, tinted by size, like every other item tile
    const gold = p.kind === "resources" && p.contents !== null ? p.contents : 0;
    const well = p.kind === "speedUp" ? { art: resourceIconUrl("speedup"), cap: fmtSpeedUp(p.minutes), rarity: amountTier("speedUp", p.minutes) }
      : { art: resourceIconUrl(p.icon), count: gold > 0 ? fmtCompact(gold) : undefined, rarity: gold <= 0 ? null : gold < 1_000 ? 2 : gold < 3_000 ? 3 : 4 };
    const html = slotTileHtml({
      well, state: locked ? "locked" : sold ? "sold" : "ok", lock: locked ? `Level ${p.opensAt}` : undefined,
      left: locked ? (p.perDay !== null ? `${p.perDay} a day` : "") : p.left !== null ? (sold ? "Sold out" : `Left: ${p.left}`) : "",
      name: p.kind === "speedUp" ? `${fmtSpeedUp(p.minutes)} speed-up` : p.name ?? "Gold", price: gemBtnHtml(p.price, "b-pack", `data-id="${p.id}"`),
      className: `pack k-${p.kind}${sold ? " sold" : ""}${p.name ? " named" : ""}`, data: `data-id="${p.id}"`,
    });
    return html;
  };
  return `<section class="ssec packs">${secHtml(title, sub, icon(ic, 18))}<div class="k-grid3 pgrid n${packs.length}">${packs.map(card).join("")}</div></section>`;
}

